import React, { useState, useEffect } from "react";
import { GitBranch, Plus, Trash2 } from "lucide-react";
import * as api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import { EmptyState } from "../shared/EmptyState";
import { PageLoader } from "../shared/LoadingSpinner";
import { TICKET_TYPES } from "../../lib/utils";
import { toast } from "sonner";

const APPROVER_TYPES = [
    { id: "specific_user", label: "Utilisateur spécifique" },
    { id: "requester_manager", label: "Manager du demandeur" },
    { id: "department_manager", label: "Manager du département" },
    { id: "resource_owner", label: "Propriétaire de la ressource" },
    { id: "it_admin", label: "Administrateur IT" },
];

export function AdminWorkflows() {
    const [chains, setChains] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedType, setSelectedType] = useState("");
    const [steps, setSteps] = useState([]);

    useEffect(() => {
        loadChains();
    }, []);

    const loadChains = async () => {
        try {
            setLoading(true);
            const data = await api.getApprovalChains();
            setChains(data);
        } catch (error) {
            toast.error("Erreur lors du chargement");
        } finally {
            setLoading(false);
        }
    };

    const handleSelectType = (typeId) => {
        setSelectedType(typeId);
        const existing = chains.find((c) => c.ticket_type === typeId);
        if (existing) {
            setSteps(existing.steps || []);
        } else {
            setSteps([]);
        }
    };

    const addStep = () => {
        setSteps([
            ...steps,
            {
                step_order: steps.length + 1,
                approver_type: "requester_manager",
                approver_id: null,
                is_required: true,
                timeout_hours: 48,
            },
        ]);
    };

    const removeStep = (index) => {
        const newSteps = steps.filter((_, i) => i !== index);
        // Reorder
        newSteps.forEach((s, i) => (s.step_order = i + 1));
        setSteps(newSteps);
    };

    const updateStep = (index, field, value) => {
        const newSteps = [...steps];
        newSteps[index][field] = value;
        setSteps(newSteps);
    };

    const handleSave = async () => {
        try {
            await api.createApprovalChain({
                ticket_type: selectedType,
                steps: steps,
            });
            toast.success("Workflow sauvegardé");
            loadChains();
        } catch (error) {
            toast.error("Erreur lors de la sauvegarde");
        }
    };

    const getTypeLabel = (typeId) => {
        return TICKET_TYPES.find((t) => t.id === typeId)?.label || typeId;
    };

    const getApproverTypeLabel = (typeId) => {
        return APPROVER_TYPES.find((t) => t.id === typeId)?.label || typeId;
    };

    if (loading) return <PageLoader />;

    return (
        <div className="page-container" data-testid="admin-workflows">
            <div className="page-header">
                <h1 className="page-title">Workflows d'approbation</h1>
                <p className="page-subtitle">
                    Configurez les chaînes d'approbation par type de ticket
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Ticket Type List */}
                <div className="card">
                    <div className="card-header">
                        <h2 className="text-sm font-semibold">Types de tickets</h2>
                    </div>
                    <div className="card-body p-0">
                        <div className="divide-y">
                            {TICKET_TYPES.map((type) => {
                                const chain = chains.find((c) => c.ticket_type === type.id);
                                const hasChain = chain && chain.steps?.length > 0;
                                return (
                                    <button
                                        key={type.id}
                                        onClick={() => handleSelectType(type.id)}
                                        className={`w-full text-left p-3 hover:bg-gray-50 transition-colors ${
                                            selectedType === type.id ? "bg-gray-50" : ""
                                        }`}
                                        data-testid={`workflow-type-${type.id}`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-medium">{type.label}</span>
                                            {hasChain && (
                                                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                                                    {chain.steps.length} étape{chain.steps.length > 1 ? "s" : ""}
                                                </span>
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Workflow Editor */}
                <div className="lg:col-span-2">
                    {!selectedType ? (
                        <div className="card">
                            <EmptyState
                                icon={GitBranch}
                                title="Sélectionnez un type"
                                description="Choisissez un type de ticket pour configurer son workflow"
                            />
                        </div>
                    ) : (
                        <div className="card">
                            <div className="card-header flex items-center justify-between">
                                <h2 className="text-sm font-semibold">
                                    Workflow: {getTypeLabel(selectedType)}
                                </h2>
                                <Button size="sm" variant="outline" onClick={addStep}>
                                    <Plus className="w-4 h-4 mr-1" />
                                    Ajouter une étape
                                </Button>
                            </div>
                            <div className="card-body">
                                {steps.length === 0 ? (
                                    <p className="text-sm text-gray-500 text-center py-8">
                                        Aucune étape d'approbation.
                                        <br />
                                        Les tickets seront directement ouverts.
                                    </p>
                                ) : (
                                    <div className="space-y-4">
                                        {steps.map((step, index) => (
                                            <div
                                                key={`step-${step.step_order}-${index}`}
                                                className="border rounded-lg p-4"
                                                data-testid={`workflow-step-${index}`}
                                            >
                                                <div className="flex items-center justify-between mb-3">
                                                    <span className="text-sm font-medium text-gray-700">
                                                        Étape {step.step_order}
                                                    </span>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => removeStep(index)}
                                                    >
                                                        <Trash2 className="w-4 h-4 text-red-500" />
                                                    </Button>
                                                </div>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <div>
                                                        <Label className="form-label">
                                                            Type d'approbateur
                                                        </Label>
                                                        <Select
                                                            value={step.approver_type}
                                                            onValueChange={(v) =>
                                                                updateStep(index, "approver_type", v)
                                                            }
                                                        >
                                                            <SelectTrigger>
                                                                <SelectValue />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {APPROVER_TYPES.map((t) => (
                                                                    <SelectItem key={t.id} value={t.id}>
                                                                        {t.label}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                    <div>
                                                        <Label className="form-label">
                                                            Délai max (heures)
                                                        </Label>
                                                        <Input
                                                            type="number"
                                                            min={0}
                                                            value={step.timeout_hours}
                                                            onChange={(e) =>
                                                                updateStep(
                                                                    index,
                                                                    "timeout_hours",
                                                                    parseInt(e.target.value) || 0
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                                <div className="mt-3">
                                                    <label className="flex items-center gap-2">
                                                        <Checkbox
                                                            checked={step.is_required}
                                                            onCheckedChange={(checked) =>
                                                                updateStep(index, "is_required", checked)
                                                            }
                                                        />
                                                        <span className="text-sm">Étape obligatoire</span>
                                                    </label>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <div className="mt-6 flex justify-end">
                                    <Button onClick={handleSave} data-testid="save-workflow-btn">
                                        Enregistrer le workflow
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
